import { useCallback, useEffect, useMemo } from "react";
import { UseQueryResult } from "@tanstack/react-query";
import { parseISO } from "date-fns";
import { JournalEntry } from "@common/types/domains/accounting/journalEntry";
import { getShortJournalEntryReferenceNumber } from "@common/helpers/accounting";
import { accountingNumberFormatter } from "@common/helpers/number";
import { sumMonetaryValues } from "@common/helpers/monetary";
import { useEntitiesById } from "@hooks/useEntities";
// oxlint-disable-next-line no-restricted-imports -- Legacy table migration/demo — do not copy into new code.
import { SimpleTable } from "@design-system/Tables/SimpleTable/SimpleTable";
import { Button } from "@design-system/Button/Button";
import { Loader } from "@design-system/Loader/Loader";
import {
    StandardModalBody,
    StandardModalHeader,
} from "@design-system/Modal/Modal";
import { formatDate } from "@helpers/date";
import { HStack } from "@design-system/HStack/HStack";
import { useOverlays } from "../../../../contexts/OverlayContext";
import styles from "./RemoveFinancialAccount.module.scss";

interface RemoveAccountManualJournalsReviewContentProps {
    journalEntries: UseQueryResult<JournalEntry[], unknown>;
    back: () => void;
    forward: () => void;
    entityId: number;
}

const columns = [
    { key: "date", label: "Date" },
    { key: "reference", label: "Reference" },
    { key: "debitCredit", label: "Debit/Credit" },
];

function formatJournalEntryDate(date: string | Date): string {
    return formatDate(date instanceof Date ? date : parseISO(date));
}

export const RemoveAccountManualJournalsReviewContent: React.FC<
    RemoveAccountManualJournalsReviewContentProps
> = ({ journalEntries, back, forward, entityId }) => {
    const entities = useEntitiesById();
    const selectedEntity = useMemo(
        () => entities.get(entityId),
        [entities, entityId],
    );

    const { openOverlay } = useOverlays();
    const onJournalEntryClick = useCallback(
        (journalEntry: JournalEntry) => {
            if (!selectedEntity) {
                return;
            }

            openOverlay("journalEntryDrawer", {
                entity: selectedEntity,
                journalEntryId: journalEntry.id,
            });
        },
        [selectedEntity, openOverlay],
    );

    useEffect(() => {
        if (journalEntries.isFetched && journalEntries.data?.length === 0) {
            forward();
        }
    }, [journalEntries, forward]);

    if (journalEntries.isLoading) {
        return <Loader />;
    }

    return (
        <>
            <StandardModalHeader>
                This account has manual journal entries
            </StandardModalHeader>
            <StandardModalBody>
                <p className={styles.modalText}>
                    The following journal entries will be deleted along with
                    this account.
                </p>
                <SimpleTable
                    className={styles.manualJournalsTable}
                    columns={columns}
                    rows={
                        journalEntries.data?.map((journalEntry) => ({
                            id: journalEntry.id,
                            cells: [
                                {
                                    value: formatJournalEntryDate(
                                        journalEntry.date,
                                    ),
                                },
                                {
                                    value: getShortJournalEntryReferenceNumber(
                                        journalEntry.id,
                                    ),
                                },
                                {
                                    value: accountingNumberFormatter(
                                        journalEntry.lines.reduce(
                                            (sum, line) =>
                                                sumMonetaryValues([
                                                    sum,
                                                    line.debitAmount ?? 0,
                                                ]),
                                            0,
                                        ),
                                    ),
                                },
                            ],
                            onClick: () => onJournalEntryClick(journalEntry),
                        })) ?? []
                    }
                    columnBorders
                />
                <HStack as="footer" justify="between">
                    <Button variant="secondary" size="xl" onClick={back}>
                        Back
                    </Button>
                    <Button variant="primary" size="xl" onClick={forward}>
                        Confirm
                    </Button>
                </HStack>
            </StandardModalBody>
        </>
    );
};
